import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Activity } from 'lucide-react';

interface UptimeStats {
  uptime_30d_pct: number | null;
  uptime_24h_pct: number | null;
  samples_30d: number;
  samples_24h: number;
  avg_latency_ms: number | null;
  p95_latency_ms: number | null;
  latest: {
    ok: boolean;
    latency_ms: number;
    checked_at: string;
    api_version: string | null;
  } | null;
  tracking_since: string | null;
}

const formatPct = (n: number | null) => (n == null ? '—' : `${n.toFixed(n >= 99.95 ? 3 : 2)}%`);

const LiveStatusStrip = () => {
  const [stats, setStats] = useState<UptimeStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data } = await supabase.functions.invoke('uptime-stats', { method: 'GET' });
      if (!cancelled && data) {
        setStats(data as UptimeStats);
        setLoading(false);
      } else if (!cancelled) {
        setLoading(false);
      }
    };
    load();
    const interval = setInterval(load, 30_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const isUp = stats?.latest?.ok ?? false;
  const latency = stats?.latest?.latency_ms ?? null;
  const version = stats?.latest?.api_version ?? 'v1';
  const hasData = (stats?.samples_30d ?? 0) > 0;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-center gap-2 mb-3">
        <Activity className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
          Live API Status
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-lg overflow-hidden border border-border">
        {/* Status */}
        <div className="bg-card p-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <div
              className={`h-2 w-2 rounded-full ${
                loading
                  ? 'bg-muted-foreground animate-pulse'
                  : isUp
                  ? 'bg-success animate-pulse'
                  : 'bg-destructive'
              }`}
            />
            <span
              className={`text-sm font-semibold ${
                loading ? 'text-muted-foreground' : isUp ? 'text-success' : 'text-destructive'
              }`}
            >
              {loading ? 'Checking' : isUp ? 'Operational' : 'Degraded'}
            </span>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Status · {version}
          </div>
        </div>

        {/* Latency */}
        <div className="bg-card p-4 text-center">
          <div className="text-sm font-semibold text-foreground font-mono">
            {latency != null ? `${latency} ms` : '—'}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
            Latency · last ping
          </div>
        </div>

        {/* Uptime 24h */}
        <div className="bg-card p-4 text-center">
          <div className="text-sm font-semibold text-foreground font-mono">
            {hasData ? formatPct(stats!.uptime_24h_pct) : '—'}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
            Uptime · 24h
          </div>
        </div>

        {/* Uptime 30d */}
        <div className="bg-card p-4 text-center">
          <div className="text-sm font-semibold text-foreground font-mono">
            {hasData ? formatPct(stats!.uptime_30d_pct) : '—'}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
            Uptime · 30d
          </div>
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground text-center mt-3">
        {hasData
          ? `Computed from ${stats!.samples_30d.toLocaleString()} healthchecks · pinged every minute`
          : 'Uptime tracking just started — figures populate within minutes.'}
        {' · Region: EU-West (Frankfurt)'}
      </p>
    </div>
  );
};

export default LiveStatusStrip;
